import { useMemo } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { PageHeader, Loading } from "@/components/portal/widgets";
import { StatusBadge, ExportButton, ErrorNote } from "@/components/founder/ui";
import { useFounder, money, fmtD, fmtMonth, daysAgo } from "@/lib/founderApi";

const SORTS = {
  created_at: ["Newest", (c) => c.created_at || ""],
  mrr: ["MRR", (c) => c.mrr || 0],
  drivers: ["Drivers", (c) => c.usage?.drivers || 0],
  vehicles: ["Vehicles", (c) => c.usage?.vehicles || 0],
  trips_30d: ["Trips (30d)", (c) => c.usage?.trips_30d || 0],
  users: ["Users", (c) => c.usage?.users || 0],
  last_active: ["Last active", (c) => c.last_active || ""],
  revenue_total: ["Revenue", (c) => c.revenue_total || 0],
};
const sel = "rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

export default function FounderCompanies() {
  const { demo } = useOutletContext();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data, error } = useFounder(`/platform/companies?include_demo=${demo}`);
  const q = params.get("q") || "", status = params.get("status") || "", plan = params.get("plan") || "", month = params.get("month") || "", sort = params.get("sort") || "created_at";
  const set = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };

  const rows = useMemo(() => {
    const list = (data || []).filter((c) => {
      const hay = `${c.name} ${c.owner?.name} ${c.owner?.email} ${c.region}`.toLowerCase();
      return (!q || hay.includes(q.toLowerCase())) && (!status || c.status === status || c.stripe_status === status)
        && (!plan || c.plan === plan) && (!month || (c.created_at || "").startsWith(month));
    });
    const key = (SORTS[sort] || SORTS.created_at)[1];
    return list.sort((a, b) => (key(a) < key(b) ? 1 : key(a) > key(b) ? -1 : 0));
  }, [data, q, status, plan, month, sort]);

  const statuses = [...new Set((data || []).map((c) => c.status).filter(Boolean))];
  const filtered = status || plan || month || q;

  return (
    <div data-testid="founder-companies">
      <PageHeader title="Companies" subtitle={data ? `${rows.length} of ${data.length} companies` : "Every company that signed up"}>
        <ExportButton kind="companies" demo={demo} />
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => set("q", e.target.value)} placeholder="Company, owner or email" className={`${sel} w-64 pl-9`} data-testid="companies-search" />
        </div>
        <select value={status} onChange={(e) => set("status", e.target.value)} className={sel} data-testid="companies-status-filter">
          <option value="">All statuses</option>
          {[...new Set(["trial", "active", "past_due", "canceled", "trial_ended", ...statuses])].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select value={plan} onChange={(e) => set("plan", e.target.value)} className={sel} data-testid="companies-plan-filter">
          <option value="">All plans</option>
          {["starter", "growth", "fleet", "enterprise"].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={sort} onChange={(e) => set("sort", e.target.value)} className={sel} data-testid="companies-sort">
          {Object.entries(SORTS).map(([k, [l]]) => <option key={k} value={k}>Sort: {l}</option>)}
        </select>
        {month && <span className="rounded-full bg-blue-600/15 px-3 py-1 text-xs font-semibold text-blue-300" data-testid="companies-month-chip">Signed up {fmtMonth(month)}</span>}
        {filtered && <button onClick={() => setParams({ sort }, { replace: true })} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="companies-clear"><X className="h-3.5 w-3.5" /> Clear filters</button>}
      </div>
      <ErrorNote error={error} />
      {!data && !error ? <Loading label="Loading companies…" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead className="border-b border-slate-800 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Company", "Owner", "Plan", "Status", "Drivers", "Vehicles", "Trips 30d", "MRR", "Signed up", "Last active"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((c) => {
                const idle = daysAgo(c.last_active);
                return (
                  <tr key={c.id} onClick={() => nav(`/founder/companies/${c.id}`)} className="cursor-pointer transition-colors hover:bg-slate-800/40" data-testid={`company-row-${c.id}`}>
                    <td className="px-4 py-3"><p className="font-semibold text-white">{c.name}{c.is_demo && <span className="ml-2 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] text-slate-300">DEMO</span>}</p><p className="text-xs text-slate-500">{c.region || "—"}</p></td>
                    <td className="px-4 py-3"><p className="text-slate-200">{c.owner?.name}</p><p className="text-xs text-slate-500">{c.owner?.email}</p></td>
                    <td className="px-4 py-3 capitalize text-slate-200">{c.plan}<span className="block text-xs normal-case text-slate-500">{money(c.plan_price)}/{c.billing_cycle === "year" ? "yr" : "mo"}</span></td>
                    <td className="px-4 py-3"><StatusBadge value={c.status} />{c.status === "trial" && c.trial_ends_at && <span className="mt-1 block text-[11px] text-slate-500">ends {fmtD(c.trial_ends_at)}</span>}</td>
                    <td className="px-4 py-3 font-mono-data">{c.usage?.drivers ?? 0}</td>
                    <td className="px-4 py-3 font-mono-data">{c.usage?.vehicles ?? 0}</td>
                    <td className="px-4 py-3 font-mono-data">{c.usage?.trips_30d ?? 0}</td>
                    <td className="px-4 py-3 font-mono-data text-emerald-300">{money(c.mrr)}</td>
                    <td className="px-4 py-3 text-slate-400">{fmtD(c.created_at)}</td>
                    <td className={`px-4 py-3 ${idle != null && idle > 7 ? "text-amber-300" : "text-slate-400"}`}>{idle == null ? "Never" : idle === 0 ? "Today" : `${idle}d ago`}</td>
                  </tr>
                );
              })}
              {!rows.length && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-500" data-testid="companies-empty">No companies match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
